import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Database, Zap, Cpu } from 'lucide-react';
export function PreFlightInterception() {
  return <section className="py-24 px-6 md:px-12 lg:px-24 bg-black text-white" data-id="element-977">
      <div className="max-w-7xl mx-auto" data-id="element-978">
        {/* Header */}
        <div className="mb-16 text-center" data-id="element-979">
          <span className="text-sm font-bold tracking-widest uppercase text-[#0066FF] mb-4 block font-sans" data-id="element-980">
            How It Works
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-8 font-['Syne']" data-id="element-981">
            What do we mean by Pre-flight?
          </h2>
          <div className="max-w-4xl mx-auto space-y-6" data-id="element-982">
            <p className="text-lg text-gray-300 leading-relaxed" data-id="element-983">
              Pre-flight is the moment before an AI model begins reasoning. It's
              where intent and context is assembled, and constraints
              are enforced—before a single token is generated.
            </p>
            <p className="text-lg text-gray-300 leading-relaxed" data-id="element-984">
  <strong data-id="element-985">Your enterprise should own everything before the model thinks.</strong>  That moment is where rules, behaviors, judgment,
  and risk are determined. Once the model starts reasoning, it has
  already made assumptions. Governance after the fact can only react
  to outcomes, not control how those outcomes were formed.
          </p>
            <p className="text-xl text-white font-semibold leading-relaxed" data-id="element-986">
              Pre-flight is the AI control plane.
            </p>
          </div>
        </div>

        {/* How Pre-flight works in Synthia */}
        <div className="mb-16" data-id="element-987">
          <h3 className="text-2xl md:text-3xl font-bold mb-12 font-['Syne'] text-white text-center" data-id="element-988">
            How Pre-flight works in Synthia
          </h3>

          {/* Three Column Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-989">
            {/* CMP Box */}
            <div className="relative group" data-id="element-990">
              <div className="absolute inset-0 bg-gradient-to-br from-[#0066FF]/20 to-transparent rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300" data-id="element-991"></div>
              <div className="relative bg-gray-900 border-2 border-[#0066FF]/50 rounded-2xl p-8 h-full hover:border-[#0066FF] transition-all duration-300 hover:transform hover:scale-105" data-id="element-992">
                <div className="flex flex-col items-center text-center h-full" data-id="element-993">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#0066FF] to-blue-700 flex items-center justify-center mb-6 shadow-lg shadow-[#0066FF]/50" data-id="element-994">
                    <Database className="w-8 h-8 text-white" data-id="element-995" />
                  </div>
                  <h4 className="text-xl font-bold mb-4 text-[#0066FF] font-['Syne']" data-id="element-996">
                    The Context Management Platform (CMP)
                  </h4>
                  <p className="text-sm text-gray-300 leading-relaxed" data-id="element-997">
                    is where enterprises decide and manage how AI is allowed to
                    think. Roles, regulatory obligations, brand standards,
                    behavioral expectations, and non-negotiables are defined as
                    durable, versioned, governed assets. This is the system of
                    record for enterprise reasoning.
                  </p>
                </div>
              </div>
            </div>

            {/* MCP Box */}
            <div className="relative group" data-id="element-998">
              <div className="absolute inset-0 bg-gradient-to-br from-[#0066FF]/20 to-transparent rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300" data-id="element-999"></div>
              <div className="relative bg-gray-900 border-2 border-[#0066FF]/50 rounded-2xl p-8 h-full hover:border-[#0066FF] transition-all duration-300 hover:transform hover:scale-105" data-id="element-1000">
                <div className="flex flex-col items-center text-center h-full" data-id="element-1001">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#0066FF] to-blue-700 flex items-center justify-center mb-6 shadow-lg shadow-[#0066FF]/50" data-id="element-1002">
                    <Zap className="w-8 h-8 text-white" data-id="element-1003" />
                  </div>
                  <h4 className="text-xl font-bold mb-4 text-[#0066FF] font-['Syne']" data-id="element-1004">
                    The Model Context Protocol (MCP)
                  </h4>
                  <p className="text-sm text-gray-300 leading-relaxed" data-id="element-1005">
                    is the pre-flight execution layer. It retrieves the relevant
                    context from the CMP, interprets role and intent, compiles
                    the applicable rules, and delivers a precise, governed
                    instruction set to the model before generation begins.
                    Nothing is improvised or left to chance.
                  </p>
                </div>
              </div>
            </div>

            {/* LLM Box */}
            <div className="relative group" data-id="element-1006">
              <div className="absolute inset-0 bg-gradient-to-br from-[#0066FF]/20 to-transparent rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300" data-id="element-1007"></div>
              <div className="relative bg-gray-900 border-2 border-[#0066FF]/50 rounded-2xl p-8 h-full hover:border-[#0066FF] transition-all duration-300 hover:transform hover:scale-105" data-id="element-1008">
                <div className="flex flex-col items-center text-center h-full" data-id="element-1009">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#0066FF] to-blue-700 flex items-center justify-center mb-6 shadow-lg shadow-[#0066FF]/50" data-id="element-1010">
                    <Cpu className="w-8 h-8 text-white" data-id="element-1011" />
                  </div>
                  <h4 className="text-xl font-bold mb-4 text-[#0066FF] font-['Syne']" data-id="element-1012">
                    The LLM
                  </h4>
                  <p className="text-sm text-gray-300 leading-relaxed" data-id="element-1013">
                    is simply the engine. It does not decide how to think. It
                    executes the thinking it has been given—guided, constrained,
                    and aligned with enterprise intent.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why Pre-flight matters */}
        <div className="mb-12 max-w-4xl mx-auto" data-id="element-1014">
          <h3 className="text-2xl md:text-3xl font-bold mb-6 font-['Syne'] text-white text-center" data-id="element-1015">
            Why Pre-flight matters
          </h3>
          <p className="text-lg text-gray-300 leading-relaxed mb-6 text-center" data-id="element-1016">
            Pre-flight turns AI from a reactive system into a governed one. It
            prevents hallucination, drift, and non-compliance by ensuring the
            model never reasons outside approved boundaries. It replaces
            post-hoc review with upstream control. And it allows enterprises to
            scale AI safely because governance is enforced by design, not by
            memory or manual checks.
          </p>
          <p className="text-xl text-white font-semibold leading-relaxed text-center" data-id="element-1017">
            Control the thinking first. Everything downstream gets better.
          </p>
        </div>

        {/* CTA Button */}
        <div className="mt-12 text-center" data-id="element-1018">
          <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-[#0066FF] text-white font-['Syne'] font-bold text-sm tracking-wide uppercase hover:bg-white hover:text-[#0066FF] transition-all duration-200 hover:scale-105" data-id="element-1019">
            BOOK A DEMO
            <ArrowRight className="w-5 h-5" data-id="element-1020" />
          </Link>
        </div>
      </div>
    </section>;
}